import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { defineMessages } from 'react-intl';
import styled from 'styled-components';

import type { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { LandingPromoBanner } from '@sorare/core/src/components/Season2026/LandingPromoBanner';
import { LogoBezelMarquee } from '@sorare/core/src/components/Season2026/LogoBezelMarquee';
import { Ambassadors } from '@sorare/core/src/components/landing/Ambassadors';
import { CommunityBlock } from '@sorare/core/src/components/landing/CommunityBlock';
import { Play } from '@sorare/core/src/components/landing/Season2026/Play';
import { RewardBanner } from '@sorare/core/src/components/landing/Season2026/RewardBanner';
import { Scout } from '@sorare/core/src/components/landing/Season2026/Scout';
import { WalletLandingHero } from '@sorare/core/src/components/landing/Season2026/WalletLandingHero';
import { Win } from '@sorare/core/src/components/landing/Season2026/Win';
import { League } from '@sorare/core/src/components/landing/types';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader.ts';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader.ts';
import { NBATeams } from '@sorare/core/src/lib/nbaTeams';
import { breakpoints } from '@sorare/core/src/style/mediaQuery';

import { LeagueImage } from '#src/components/league/LeagueImage/index.tsx';

import type {
  WalletLandingBranding,
  WalletLandingQuery,
  WalletLandingQueryVariables,
} from './__generated__/index.graphql.ts';

const ASSETS = import.meta.glob<string>('./assets/**/*.{webp,svg}', {
  eager: true,
  query: '?url',
  import: 'default',
});

type Variant =
  | 'football'
  | 'nba'
  | 'laliga'
  | 'bundesliga'
  | 'premier-league'
  | 'ligue1';

const asset = (variant: Variant, file: string): string =>
  ASSETS[`./assets/${variant}/${file}`] ?? ASSETS[`./assets/football/${file}`];

const VARIANT_TO_LEAGUE: Record<Variant, League> = {
  football: League.Global,
  nba: League.NBA,
  laliga: League.LaLiga,
  bundesliga: League.Bundesliga,
  'premier-league': League.PremierLeague,
  ligue1: League.Ligue1,
};

// The football and nba variants have no domestic-league override; they read the
// competition-agnostic default assets (server-side fallback).
const VARIANT_TO_COMPETITION_SLUG: Partial<Record<Variant, string>> = {
  laliga: 'laliga-es',
  bundesliga: 'bundesliga-de',
  'premier-league': 'premier-league-gb-eng',
  ligue1: 'ligue-1-fr',
};

const BRANDING_FRAGMENT = gql`
  fragment WalletLandingBranding on CardSetBranding {
    id
    heroBackground: assetUrl(
      slot: LANDING_HERO_BACKGROUND
      competitionSlug: $competitionSlug
    )
    heroLeftPlayers: assetUrl(
      slot: LANDING_HERO_PLAYERS_LEFT
      competitionSlug: $competitionSlug
    )
    heroRightPlayers: assetUrl(
      slot: LANDING_HERO_PLAYERS_RIGHT
      competitionSlug: $competitionSlug
    )
    scoutBackground: assetUrl(
      slot: LANDING_SCOUT_BACKGROUND
      competitionSlug: $competitionSlug
    )
    scoutPlayers: assetUrl(
      slot: LANDING_SCOUT_PLAYERS
      competitionSlug: $competitionSlug
    )
    playBackground: assetUrl(
      slot: LANDING_PLAY_BACKGROUND
      competitionSlug: $competitionSlug
    )
    playLineup: assetUrl(
      slot: LANDING_PLAY_LINEUP
      competitionSlug: $competitionSlug
    )
    rewardBackground: assetUrl(
      slot: LANDING_REWARD_BACKGROUND
      competitionSlug: $competitionSlug
    )
  }
` as TypedDocumentNode<WalletLandingBranding>;

const WALLET_LANDING_QUERY = gql`
  query WalletLandingQuery($sport: Sport!, $competitionSlug: String) {
    cardSet {
      id
      currentSet(sport: $sport) {
        slug
        branding {
          id
          ...WalletLandingBranding
        }
      }
      previousSet(sport: $sport) {
        slug
        branding {
          id
          ...WalletLandingBranding
        }
      }
    }
  }
  ${BRANDING_FRAGMENT}
` as TypedDocumentNode<WalletLandingQuery, WalletLandingQueryVariables>;

type PlayerSize = { width: number; height: number };
const FOOTBALL_PLAYER_SIZE: { left: PlayerSize; right: PlayerSize } = {
  left: { width: 561, height: 509 },
  right: { width: 540, height: 510 },
};
const PLAYER_SIZES: Partial<
  Record<Variant, { left: PlayerSize; right: PlayerSize }>
> = {
  laliga: {
    left: { width: 670, height: 477 },
    right: { width: 657, height: 488 },
  },
  bundesliga: {
    left: { width: 564, height: 575 },
    right: { width: 484, height: 563 },
  },
  ligue1: {
    left: { width: 658, height: 430 },
    right: { width: 557, height: 432 },
  },
};

const LOGOS: Partial<Record<Variant, { file: string; alt: string }>> = {
  nba: { file: 'logo.svg', alt: 'NBA' },
  laliga: { file: 'league-logo.webp', alt: 'LALIGA' },
  bundesliga: { file: 'league-logo.webp', alt: 'Bundesliga' },
  'premier-league': { file: 'league-logo.webp', alt: 'Premier League' },
  ligue1: { file: 'league-logo.webp', alt: 'Ligue 1' },
};

type WinItem = {
  name: string;
  file: string;
  width: number;
  height: number;
  gridArea: string;
};

const WIN_ITEMS: WinItem[] = [
  {
    name: 'plane',
    file: 'plane.webp',
    width: 320,
    height: 257,
    gridArea: '1 / 1 / 2 / 2',
  },
  {
    name: 'jersey',
    file: 'jersey.webp',
    width: 274,
    height: 229,
    gridArea: '2 / 1 / 3 / 2',
  },
  {
    name: 'coin',
    file: 'coin.webp',
    width: 285,
    height: 257,
    gridArea: '2 / 3 / 3 / 4',
  },
  {
    name: 'box',
    file: 'box.webp',
    width: 320,
    height: 257,
    gridArea: '1 / 5 / 2 / 6',
  },
  {
    name: 'ticket',
    file: 'ticket.webp',
    width: 320,
    height: 257,
    gridArea: '2 / 5 / 3 / 6',
  },
  {
    name: 'coin2',
    file: 'coin.webp',
    width: 285,
    height: 257,
    gridArea: '1 / 7 / 2 / 8',
  },
];

const NBA_WIN_ITEMS: WinItem[] = [
  {
    name: 'allStars',
    file: 'allStars.webp',
    width: 320,
    height: 257,
    gridArea: '1 / 2 / 3 / 3',
  },
  {
    name: 'allStars2',
    file: 'allStars.webp',
    width: 320,
    height: 257,
    gridArea: '1 / 6 / 3 / 7',
  },
  {
    name: 'rewardCards',
    file: 'rewardCards.webp',
    width: 320,
    height: 228,
    gridArea: '1 / 4 / 3 / 5',
  },
  {
    name: 'rewardCards2',
    file: 'rewardCards.webp',
    width: 320,
    height: 228,
    gridArea: '1 / 8 / 3 / 9',
  },
];

const FOOTBALL_SCOUT_MARQUEE = [
  {
    id: 'u23',
    file: 'thumbnail_u23.webp',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-under_twenty_one/header.svg',
  },
  {
    id: 'seasonal-spain',
    file: 'thumbnail_laliga.webp',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/header.svg',
  },
  {
    id: 'seasonal-germany',
    file: 'thumbnail_bundesliga.webp',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/header.svg',
  },
  {
    id: 'ligue-1',
    file: 'thumbnail_ligue1.webp',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/first_division-france/header.svg',
  },
];

const messages = defineMessages({
  heroDescriptionFootball: {
    defaultMessage:
      'Build your 5-player lineups. When they shine on the field, you earn rewards!',
  },
  heroDescriptionNba: {
    defaultMessage:
      'Build your 5-player lineups. When they shine on the court, you earn rewards!',
  },
  playDescriptionFootball: {
    defaultMessage:
      'Select your top players each Game Week. Their scores reflect their real-life performances.',
  },
  playDescriptionNba: {
    defaultMessage:
      'Pick your best five each Game Week. When they ball, you win.',
  },
  winDescriptionNba: {
    defaultMessage:
      'Tickets, VIP experiences, cash or ETH - win it all! You may even score tickets to NBA All-Star Weekend!',
  },
  scout: { defaultMessage: 'Open your packs' },
  scoutDescription: {
    defaultMessage: 'Scout the best players everyday to complete your team',
  },
  scoutButton: { defaultMessage: 'Create your team' },
  play: { defaultMessage: 'Build your lineup' },
  win: { defaultMessage: 'Win Epic Rewards' },
});

const Root = styled(Vertical)`
  background: var(--c-black);
  padding: var(--double-unit) 0;
  &.showFAQ {
    padding-bottom: calc(8 * var(--unit));
  }
`;

const walletLandingVariables = (
  variant: Variant
): WalletLandingQueryVariables => ({
  sport: variant === 'nba' ? Sport.NBA : Sport.FOOTBALL,
  competitionSlug: VARIANT_TO_COMPETITION_SLUG[variant] ?? null,
});

// Loaded by the landing route so the branding art is already there on first
// paint, instead of arriving after mount and shifting the layout.
export const walletLandingLoader = (variant: Variant) =>
  queryLoader(WALLET_LANDING_QUERY, () => walletLandingVariables(variant));

const brandingFromQuery = (data: WalletLandingQuery | undefined) => {
  const cardSet = data?.cardSet;

  return cardSet?.currentSet?.branding ?? cardSet?.previousSet?.branding;
};

type Props = {
  variant?: Variant;
  branding?: WalletLandingBranding;
  leaderboards: So5LeaderboardType[];
  showFAQ?: boolean;
  faq?: ReactNode;
};

export const WalletLanding = ({
  variant = 'football',
  branding,
  leaderboards,
  showFAQ,
  faq,
}: Props) => {
  const {
    flags: { useLandingPagePromoBanner = false },
  } = useFeatureFlags();
  const league = VARIANT_TO_LEAGUE[variant];
  const sport = variant === 'nba' ? Sport.NBA : Sport.FOOTBALL;
  const isNba = variant === 'nba';
  const playerSize = PLAYER_SIZES[variant] ?? FOOTBALL_PLAYER_SIZE;
  const logo = LOGOS[variant];
  const isFootball = variant === 'football';

  const showPromoBanner = isNba || useLandingPagePromoBanner;

  const leagueLogo = logo ? (
    <Img
      src={asset(variant, logo.file)}
      alt={logo.alt}
      width="240"
      height="80"
    />
  ) : undefined;

  const winItems = [...WIN_ITEMS, ...NBA_WIN_ITEMS].map(
    ({ name, file, ...rest }) => ({
      name,
      src: asset(variant, file),
      ...rest,
    })
  );

  return (
    <Root
      gap={8}
      className={classNames({ extraBottomPadding: !showFAQ }, 'dark-mode')}
    >
      <Vertical>
        {!!showPromoBanner && (
          <Container maxWidth={breakpoints.mediumDesktop}>
            <LandingPromoBanner league={league} />
          </Container>
        )}
        <section>
          <Vertical gap={4}>
            <WalletLandingHero
              assets={{
                background: branding?.heroBackground ?? undefined,
                players: [
                  {
                    pictureUrl: branding?.heroLeftPlayers ?? undefined,
                    ...playerSize.left,
                  },
                  {
                    pictureUrl: branding?.heroRightPlayers ?? undefined,
                    ...playerSize.right,
                  },
                ],
              }}
              leagueLogo={leagueLogo}
              league={league}
              messages={{
                hero: glossary.landingHero,
                heroWallet: glossary.landingHeroWallet,
                heroWalletPlaceholder: glossary.landingHeroWalletPlaceholder,
                heroDescription: isNba
                  ? messages.heroDescriptionNba
                  : messages.heroDescriptionFootball,
              }}
            />
            {!!isNba && (
              <LogoBezelMarquee
                leagues={NBATeams.map(({ fullName, svgUrl }) => ({
                  displayName: fullName,
                  pictureSecondaryUrl: svgUrl,
                }))}
              />
            )}
          </Vertical>
        </section>
      </Vertical>
      <section>
        <Scout
          style={{ color: isFootball ? 'var(--c-black)' : undefined }}
          assets={{
            background: branding?.scoutBackground ?? undefined,
            players: branding?.scoutPlayers ?? undefined,
          }}
          league={league}
          messages={{
            scout: messages.scout,
            scoutDescription: messages.scoutDescription,
            scoutButton: messages.scoutButton,
          }}
        >
          {sport === Sport.FOOTBALL &&
            !isFootball &&
            FOOTBALL_SCOUT_MARQUEE.map(({ id, file, headerUrl }) => (
              <LeagueImage
                key={id}
                pictureUrl={asset('football', file)}
                headerUrl={headerUrl}
              />
            ))}
        </Scout>
      </section>
      <section>
        <Play
          style={{ color: isFootball ? 'var(--c-black)' : undefined }}
          assets={{
            background: branding?.playBackground ?? undefined,
            lineup: branding?.playLineup ?? undefined,
          }}
          league={league}
          messages={{
            play: messages.play,
            playDescription: isNba
              ? messages.playDescriptionNba
              : messages.playDescriptionFootball,
          }}
        />
      </section>
      <section>
        {isNba ? (
          <Win
            league={league}
            assets={winItems}
            messages={{
              win: messages.win,
              winDescription: messages.winDescriptionNba,
              marquee: glossary.landingHero,
            }}
          />
        ) : (
          <RewardBanner
            league={league}
            background={branding?.rewardBackground ?? undefined}
          />
        )}
      </section>
      <section
        style={{ marginTop: isNba ? 'calc(var(--unit) * -20)' : undefined }}
      >
        <CommunityBlock
          leaderboards={leaderboards.map(leaderboardType => ({
            sport,
            leaderboardType,
          }))}
        />
      </section>
      <section>
        <Container>
          <Ambassadors />
        </Container>
      </section>
      {!!showFAQ && faq}
    </Root>
  );
};

type BrandedProps = Omit<Props, 'branding'> & {
  // The root page renders the landing as a child rather than as its own route,
  // so there is no loader of ours to read and the branding has to be queried.
  withoutLoader?: boolean;
};

const WalletLandingFromLoader = (props: BrandedProps) => {
  const { data } = useQueryLoader<WalletLandingQuery>();

  return <WalletLanding {...props} branding={brandingFromQuery(data)} />;
};

const WalletLandingFromQuery = ({
  variant = 'football',
  ...props
}: BrandedProps) => {
  const { data } = useQuery(WALLET_LANDING_QUERY, {
    variables: walletLandingVariables(variant),
    errorPolicy: 'all',
  });

  return (
    <WalletLanding
      {...props}
      variant={variant}
      branding={brandingFromQuery(data)}
    />
  );
};

// The landing is both a route of its own, which preloads the branding in its
// loader, and a child of the root page, which has no loader to read from.
export const WalletLandingWithBranding = ({
  withoutLoader,
  ...props
}: BrandedProps) =>
  withoutLoader ? (
    <WalletLandingFromQuery {...props} />
  ) : (
    <WalletLandingFromLoader {...props} />
  );
